import React, { useEffect, useRef } from 'react';

interface AudioVisualizerCanvasProps {
  isRecording: boolean;
  waveHeights: number[];
}

export function AudioVisualizerCanvas({ isRecording, waveHeights }: AudioVisualizerCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Set display sizes
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    let animationId: number;
    let phase = 0;

    const render = () => {
      const width = rect.width;
      const height = rect.height;
      
      ctx.clearRect(0, 0, width, height);
      const centerY = height / 2;

      // Draw subtle gridlines (cyber background)
      ctx.strokeStyle = 'rgba(6, 182, 212, 0.05)';
      ctx.lineWidth = 1;
      for (let y = 0; y < height; y += 12) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
      for (let x = 0; x < width; x += 40) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }

      if (isRecording) {
        phase += 0.15;
        
        // Background frequency bar spectrum
        const barWidth = width / waveHeights.length;
        const grad = ctx.createLinearGradient(0, 0, 0, height);
        grad.addColorStop(0, 'rgba(6, 182, 212, 0.15)');
        grad.addColorStop(1, 'rgba(99, 102, 241, 0.01)');
        ctx.fillStyle = grad;
        
        for (let i = 0; i < waveHeights.length; i++) {
          const rawHeight = waveHeights[i] || 15;
          const barHeight = (rawHeight / 40) * (height * 0.84);
          ctx.fillRect(i * barWidth + 1.5, centerY - barHeight / 2, barWidth - 3, barHeight);
        }

        // Overlay Carrier wave 1 (glowing cyan)
        ctx.strokeStyle = '#06b6d4';
        ctx.lineWidth = 1.8;
        ctx.shadowColor = 'rgba(6, 182, 212, 0.3)';
        ctx.shadowBlur = 8;
        ctx.beginPath();
        for (let x = 0; x < width; x++) {
          const percentage = x / width;
          const index = Math.min(Math.floor(percentage * waveHeights.length), waveHeights.length - 1);
          const currentHeight = waveHeights[index] || 15;
          const amplitude = (currentHeight / 35) * (height / 2.8);
          // Modulate with sine wave and envelope profile
          const y = centerY + Math.sin(percentage * Math.PI * 4 + phase) * amplitude * Math.sin(percentage * Math.PI);
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();

        // Overlay Carrier wave 2 (pink sub-frequency)
        ctx.strokeStyle = '#e840f3';
        ctx.lineWidth = 1.2;
        ctx.shadowColor = 'rgba(232, 64, 243, 0.2)';
        ctx.shadowBlur = 4;
        ctx.beginPath();
        for (let x = 0; x < width; x++) {
          const percentage = x / width;
          const index = Math.min(Math.floor((1 - percentage) * waveHeights.length), waveHeights.length - 1);
          const currentHeight = waveHeights[index] || 15;
          const amplitude = (currentHeight / 35) * (height / 3.4);
          const y = centerY + Math.cos(percentage * Math.PI * 5.5 - phase) * amplitude * Math.sin(percentage * Math.PI);
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();

        // Reset shadow
        ctx.shadowBlur = 0;
      } else {
        // IDLE STATE - Calm, ambient breathing curves
        phase += 0.025;

        const subWaves = [
          { color: 'rgba(6, 182, 212, 0.35)', freq: 2, amp: 8, speed: 1, width: 1.5 },
          { color: 'rgba(232, 64, 243, 0.2)', freq: 3.2, amp: 5, speed: -1.3, width: 1 },
          { color: 'rgba(99, 102, 241, 0.15)', freq: 1.4, amp: 11, speed: 0.6, width: 1 }
        ];

        subWaves.forEach(w => {
          ctx.strokeStyle = w.color;
          ctx.lineWidth = w.width;
          ctx.beginPath();
          for (let x = 0; x < width; x++) {
            const percentage = x / width;
            const y = centerY + Math.sin(percentage * Math.PI * w.freq + phase * w.speed) * w.amp * Math.sin(percentage * Math.PI);
            if (x === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          }
          ctx.stroke();
        });

        // Small digital metrics ticker overlay
        ctx.fillStyle = 'rgba(6, 182, 212, 0.4)';
        ctx.font = '7px monospace';
        ctx.fillText('STBY', width - 24, 10);
        ctx.fillText('0.8S SLA', 6, height - 6);

        // Passive floating grid data nodes
        ctx.fillStyle = 'rgba(6, 182, 212, 0.5)';
        for (let i = 0; i < 6; i++) {
          const x = (width * 0.12) + (Math.sin(phase * 0.4 + i) * width * 0.04) + (i * width * 0.15);
          const y = centerY + Math.cos(phase * 0.9 + i) * 5;
          ctx.beginPath();
          ctx.arc(x, y, 1.2, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      animationId = requestAnimationFrame(render);
    };

    render();

    const handleResize = () => {
      const newRect = canvas.getBoundingClientRect();
      canvas.width = newRect.width * dpr;
      canvas.height = newRect.height * dpr;
      ctx.restore();
      ctx.save();
      ctx.scale(dpr, dpr);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', handleResize);
    };
  }, [isRecording, waveHeights]);

  return (
    <canvas
      ref={canvasRef}
      id="voice-directive-canvas"
      className="absolute inset-0 w-full h-full pointer-events-none"
    />
  );
}
